import { useMemo, useState } from 'react';
import {
  Badge,
  FormControl,
  Button,
  Square,
  Avatar,
  Flex,
  Box,
  Text,
  VStack,
  useToast,
  HStack,
} from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { UserRoundPlus } from 'lucide-react';
import { User as Consultant } from '@duke-hq/svc-api-db';
import { trpcTenant } from '@duke-hq/app-provider-web/tenant/trpcTenant';
import { getUserFullName } from '@duke-hq/lib';
import { useAuthTenant } from '../useAuthTenant';
import { useDebounceCallback } from 'usehooks-ts';
import { AsyncSelect, chakraComponents, OptionProps } from 'chakra-react-select';
import { ProviderUserRoleColorSchemes } from '@duke-hq/shared-constants/providers';
import type { RouterOutputs } from '@duke-hq/svc-api/provider';

interface ConsultantOption extends Partial<Consultant> {
  label: string;
  fullname: string;
}

function Option(props: OptionProps<ConsultantOption>) {
  return (
    <chakraComponents.Option {...props}>
      <Flex align="center" justify="space-between" w="full">
        <Flex align="center">
          <Square size="32px">
            <Avatar
              size="sm"
              name={props.data.fullname}
              src={props.data.picture_url ?? undefined}
            />
          </Square>
          <VStack align="flex-start" gap={0} ml={4}>
            <Text fontWeight="medium">{`${props.data.label}`}</Text>
            <Text
              fontSize="sm"
              color={props.isSelected ? 'fg.inverse-subtle' : 'fg.muted'}
            >{`${props.data.email}`}</Text>
          </VStack>
        </Flex>
        <Flex align="flex-end">
          <Badge
            borderRadius="full"
            colorScheme={
              (props.data.role && ProviderUserRoleColorSchemes[props.data.role]) ?? 'gray'
            }
            variant="muted"
            textTransform="none"
          >
            {props.data.role?.replace(/_/g, ' ')}
          </Badge>
        </Flex>
      </Flex>
    </chakraComponents.Option>
  );
}

export function StudentSettings({
  student,
}: {
  student: NonNullable<RouterOutputs['users']['studentById']>;
}): React.ReactElement {
  const auth = useAuthTenant();
  const toast = useToast();
  const utils = trpcTenant.useUtils();
  const [ownerSelected, setOwnerSelected] = useState(student?.studentData?.owned_by);

  const { data: consultants } = trpcTenant.users.allUsersAtAvailableSiteLocations.useQuery();

  const reAssignOwner = trpcTenant.users.assignOwner.useMutation({
    onSuccess: () => {
      toast({
        title: t`Participant Reassigned successfully.`,
        status: 'success',
        isClosable: true,
      });

      void utils.users.listStudentsWithPagination.refetch();
      void utils.users.studentById.refetch({ id: student.id });
    },
  });

  const ownerOptions: ConsultantOption[] = useMemo(() => {
    if (consultants) {
      return consultants.map(
        (consultant) =>
          ({
            role: consultant.role,
            email: consultant.email,
            value: consultant.id,
            label: `${getUserFullName(consultant)} ${
              auth?.user.id === consultant.id ? `(${t`myself`})` : ''
            }`,
            fullname: `${getUserFullName(consultant)}`,
          }) as ConsultantOption,
      );
    }
    return [];
  }, [consultants, auth?.user.id]);

  const loadOwnersOptions = useDebounceCallback((inputValue: string, callback: any) => {
    if (ownerOptions) {
      callback(
        ownerOptions.filter(
          (option: any) =>
            option.label.toLowerCase().includes(inputValue.toLowerCase()) ||
            option.email.toLowerCase().includes(inputValue.toLowerCase()),
        ),
      );
    } else {
      callback([]);
    }
  }, 500);

  return (
    <Flex justify="center" align="center" w="full" p="16px">
      <FormControl id="select_student">
        <Box w="full" py={3}>
          <Text fontWeight="medium" color="fg.emphasized">
            <Trans>Reassign owner</Trans>
          </Text>
        </Box>
        <HStack justify="space-between">
          <Box w="full">
            <AsyncSelect
              name="owned_by"
              placeholder={t`Select consultant`}
              defaultOptions={ownerOptions}
              loadOptions={loadOwnersOptions}
              onChange={(val: any) => {
                setOwnerSelected(val?.value);
              }}
              value={ownerOptions.find((option: any) => option.value === ownerSelected)}
              defaultValue={ownerOptions.find((option: any) => option.value === auth.user.id)}
              size="lg"
              selectedOptionColorScheme={'brand'}
              components={{ Option }}
            />
          </Box>

          <Button
            aria-label={t`Assign owner`}
            size={'lg'}
            variant="solid"
            type="button"
            leftIcon={<UserRoundPlus />}
            isDisabled={ownerSelected === student?.studentData?.owned_by}
            isLoading={reAssignOwner.isPending}
            loadingText="Submitting"
            onClick={async () => {
              const data = {
                id: student.id,
                tenant_id: Number(student.tenant_id),
                studentData: {
                  owned_by: ownerSelected ?? '',
                },
              };
              reAssignOwner.mutate(data);
            }}
          >
            <Trans>Reassign</Trans>
          </Button>
        </HStack>
      </FormControl>
    </Flex>
  );
}
